/**
 * @file ShortageDialog.tsx
 * @description Диалог недостачи по строке задания (phase19.14, экран B шага 0).
 *
 * «Собрано 2 из 3. Не хватает: 1. Причина: … + комментарий». Количество
 * подставляется как «план минус собрано», но правится: кладовщик мог собрать
 * часть и знать, что остального нет вовсе.
 *
 * `fullScreen` на <sm — правило mobile-first для диалогов.
 */

import { useEffect, useState } from "react";

import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import { cn } from "@/lib/utils";
import {
  PICKING_SHORTAGE_REASONS,
  PICKING_SHORTAGE_REASON_LABEL,
  formatPickItemName,
  isShortageNoteRequired,
  type PickingShortageReason,
  type PickingTaskItemDto,
} from "../../../lib/api-picking";
import { formatItemQty, formatPlannedQty } from "../../../lib/picking-format";
import { qtyInputProps, validateQty } from "../../../lib/qty-input";

interface Props {
  item: PickingTaskItemDto;
  saving: boolean;
  /** Ошибка сервера (422 `shortage_quantity_invalid` и т. п.). */
  error: string | null;
  onSubmit: (input: {
    shortageQty: number;
    reason: PickingShortageReason;
    note?: string;
  }) => void;
  onClose: () => void;
}

export function ShortageDialog({ item, saving, error, onSubmit, onClose }: Props) {
  const planned = Number(item.qtyPlanned);
  const picked = Number(item.qtyPicked);
  const [qty, setQty] = useState(() => String(Math.max(planned - picked, 0) || planned));
  const [reason, setReason] = useState<PickingShortageReason>("NOT_FOUND");
  const [note, setNote] = useState(item.note ?? "");
  const [localError, setLocalError] = useState<string | null>(null);
  // Недостача измеряется в той же единице, что план строки: у фасовок это
  // штуки, и стрелка поля обязана шагать на пачку, а не на тысячную.
  const qtyProps = qtyInputProps(item.unit);

  useEffect(() => setLocalError(null), [qty, reason, note]);

  function submit() {
    const message = validateQty(qty, item.unit, {
      min: 0,
      max: planned,
      allowZero: false,
    });
    if (message) {
      setLocalError(message);
      return;
    }
    const value = Number(qty.replace(",", "."));
    if (isShortageNoteRequired(reason) && !note.trim()) {
      setLocalError("Для причины «Другое» комментарий обязателен.");
      return;
    }
    onSubmit({ shortageQty: value, reason, note: note.trim() || undefined });
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Недостача по позиции</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 overflow-y-auto">
          <div className="rounded-xl bg-surface-low p-3 text-sm">
            <div className="font-medium">{formatPickItemName(item)}</div>
            <div className="mt-1 text-on-surface-variant">
              Собрано {formatItemQty(item.qtyPicked, item.qtyPlanned, item.unit)}.{" "}
              Не хватает: {formatPlannedQty(Math.max(planned - picked, 0), item.unit)}.
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="shortage-qty">Сколько не хватило</Label>
            <input
              id="shortage-qty"
              type="number"
              inputMode={qtyProps.inputMode}
              step={qtyProps.step}
              min={0}
              max={planned}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              disabled={saving}
              className="min-h-[56px] rounded-xl border border-on-surface/15 bg-surface-lowest px-3 text-base"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Причина</Label>
            <div className="flex flex-wrap gap-2">
              {PICKING_SHORTAGE_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  aria-pressed={reason === r}
                  className={cn(
                    "min-h-12 rounded-full px-4 text-sm transition-colors",
                    reason === r
                      ? "bg-primary text-on-primary font-medium"
                      : "bg-surface-low text-on-surface-variant",
                  )}
                >
                  {PICKING_SHORTAGE_REASON_LABEL[r]}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="shortage-note">
              Комментарий{isShortageNoteRequired(reason) ? " *" : " (опц.)"}
            </Label>
            <textarea
              id="shortage-note"
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={saving}
              placeholder="Что именно не так — менеджер будет решать по этому тексту"
              className="rounded-xl border border-on-surface/15 bg-surface-lowest p-3 text-base"
            />
          </div>

          {localError ?? error ? (
            <Alert severity="error">{localError ?? error}</Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" className="min-h-12" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button className="min-h-12" onClick={submit} disabled={saving}>
            {saving ? "Сохраняем…" : "Отметить недостачу"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
